import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Search, Plus, Users, Mail, Phone, Building, Star, Eye, Trash2, Tag, X, RefreshCw, Cloud, Loader2 } from 'lucide-react'
import api from '../lib/api'
import { format } from 'date-fns'

const Leads = () => {
  const navigate = useNavigate()
  const [leads, setLeads] = useState([])
  const [loading, setLoading] = useState(true)
  const [tableLoading, setTableLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [initialLoad, setInitialLoad] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [tagFilter, setTagFilter] = useState('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showTagModal, setShowTagModal] = useState(false)
  const [selectedLead, setSelectedLead] = useState(null)
  const [newTag, setNewTag] = useState('')
  const [syncStatus, setSyncStatus] = useState(null)
  const [availableTags, setAvailableTags] = useState(['Hot', 'Warm', 'Cold', 'Enterprise', 'SMB', 'Qualified', 'Unqualified'])
  const [sortBy, setSortBy] = useState('updatedAt')
  const [sortOrder, setSortOrder] = useState('desc')
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [totalCount, setTotalCount] = useState(0)
  const observerRef = useRef(null)
  const searchTimeoutRef = useRef(null)
  const [newLead, setNewLead] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    designation: '',
    source: 'Manual Entry',
    status: 'New'
  })

  const ITEMS_PER_PAGE = 50

  // Debounce search term - only update debouncedSearch, don't clear leads here
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm)
    }, 400)
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchTerm])

  const fetchLeads = async (pageNum = 1, append = false, isInitial = false) => {
    if (isInitial) {
      setLoading(true)
    } else if (pageNum === 1 && !append) {
      setTableLoading(true)
    } else {
      setLoadingMore(true)
    }
    try {
      const params = {
        status: statusFilter === 'all' ? undefined : statusFilter,
        tags: tagFilter === 'all' ? undefined : tagFilter,
        sortBy: sortBy,
        search: debouncedSearch || undefined,
        limit: ITEMS_PER_PAGE,
        page: pageNum
      }
      const response = await api.get('/crm/leads', { params })
      const data = response.data?.leads || response.data || []
      const pagination = response.data?.pagination || {}
      
      if (append) {
        setLeads(prev => [...prev, ...data])
      } else {
        setLeads(data)
      }
      
      setHasMore(pagination.hasMore || data.length === ITEMS_PER_PAGE)
      setTotalCount(pagination.totalCount || data.length)
      setPage(pageNum)
    } catch (error) {
      console.error('Failed to fetch leads:', error)
      if (pageNum === 1) {
        toast.error('Failed to fetch leads.')
        setLeads([])
      }
    } finally {
      setLoading(false)
      setTableLoading(false)
      setLoadingMore(false)
      setInitialLoad(false)
    }
  }

  // Initial load only
  useEffect(() => {
    fetchLeads(1, false, true)
  }, [])

  // Filter/search changes - update table without full page reload
  useEffect(() => {
    if (!initialLoad) {
      setPage(1)
      fetchLeads(1, false, false)
    }
  }, [statusFilter, tagFilter, sortBy, debouncedSearch])
  
  // Fetch sync status only on mount
  useEffect(() => {
    fetchSyncStatus()
  }, [])

  const fetchSyncStatus = async () => {
    try {
      const response = await api.get('/crm/sync/status')
      setSyncStatus(response.data)
    } catch (error) {
      console.error('Failed to fetch sync status:', error)
    }
  }

  const handleSyncFromZoho = async () => {
    setSyncing(true)
    try {
      const response = await api.post('/crm/zoho/import')
      toast.success(`Synced ${response.data.imported} new leads, updated ${response.data.updated} existing leads from Zoho CRM`)
      fetchLeads()
      fetchSyncStatus()
    } catch (error) {
      console.error('Failed to sync from Zoho:', error)
      toast.error(error.response?.data?.error || 'Failed to sync leads from Zoho CRM')
    } finally {
      setSyncing(false)
    }
  }

  const handleCreateLead = async (e) => {
    e.preventDefault()
    try {
      await api.post('/crm/leads', newLead)
      toast.success('Lead created successfully!')
      setShowAddModal(false)
      setNewLead({
        name: '',
        email: '',
        phone: '',
        company: '',
        designation: '',
        source: 'Manual Entry',
        status: 'New'
      })
      fetchLeads()
    } catch (error) {
      console.error('Failed to create lead:', error)
      toast.error(error.response?.data?.error || 'Failed to create lead. Please try again.')
    }
  }

  const handleDeleteLead = async (leadId, lead) => {
    // If lead is synced with Zoho CRM, show message to delete from Zoho
    if (lead?.crmId) {
      toast.error('This lead is synced with Zoho CRM. Please delete it from Zoho CRM directly.')
      return
    }
    
    if (!confirm('Are you sure you want to delete this lead?')) return
    
    try {
      await api.delete(`/crm/leads/${leadId}`)
      toast.success('Lead deleted successfully')
      fetchLeads()
    } catch (error) {
      console.error('Failed to delete lead:', error)
      toast.error('Failed to delete lead')
    }
  }

  const handleContactLead = (lead) => {
    if (lead.email) {
      window.location.href = `mailto:${lead.email}?subject=Follow up from ${lead.source || 'our team'}`
    } else if (lead.phone) {
      window.location.href = `tel:${lead.phone}`
    } else {
      toast.error('No contact information available')
    }
  }

  const handleAddTag = async () => {
    if (!newTag.trim() || !selectedLead) return
    
    try {
      const updatedTags = [...(selectedLead.tags || []), newTag.trim()]
      await api.put(`/crm/leads/${selectedLead._id}`, { tags: updatedTags })
      toast.success('Tag added successfully')
      setNewTag('')
      setShowTagModal(false)
      setSelectedLead(null)
      fetchLeads()
    } catch (error) {
      console.error('Failed to add tag:', error)
      toast.error('Failed to add tag')
    }
  }

  const handleRemoveTag = async (leadId, tagToRemove) => {
    try {
      const lead = leads.find(l => l._id === leadId)
      const updatedTags = lead.tags.filter(tag => tag !== tagToRemove)
      await api.put(`/crm/leads/${leadId}`, { tags: updatedTags })
      fetchLeads()
    } catch (error) {
      console.error('Failed to remove tag:', error)
    }
  }

  // Infinite scroll observer
  const lastLeadRef = useCallback(node => {
    if (loading || loadingMore) return
    if (observerRef.current) observerRef.current.disconnect()
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        fetchLeads(page + 1, true)
      }
    })
    if (node) observerRef.current.observe(node)
  }, [loading, loadingMore, hasMore, page])

  // Apply sort order client-side if needed
  const displayLeads = sortOrder === 'asc' ? [...leads].reverse() : leads

  const getStatusColor = (status) => {
    const colors = {
      'New': 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300',
      'Contacted': 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300',
      'Qualified': 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300',
      'Proposal Sent': 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300',
      'Negotiation': 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300',
      'Won': 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300',
      'Lost': 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300',
      'Nurturing': 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
      'Attempted to Contact': 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300',
      'Not Contacted': 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300',
      'Contact in Future': 'bg-cyan-100 dark:bg-cyan-900/50 text-cyan-700 dark:text-cyan-300',
      'Junk Lead': 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300',
      'Lost Lead': 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300',
      'Converted': 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300',
      'Responded': 'bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300',
      'Pre-Qualified': 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300'
    }
    return colors[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
  }

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-50'
    if (score >= 60) return 'text-yellow-600 bg-yellow-50'
    return 'text-red-600 bg-red-50'
  }

  if (loading && initialLoad) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Leads</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage your sales pipeline and contacts</p>
          {syncStatus && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              <Cloud className="w-4 h-4 inline mr-1" />
              {syncStatus.synced} of {syncStatus.total} synced with Zoho CRM
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button 
            onClick={handleSyncFromZoho}
            disabled={syncing}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`w-5 h-5 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync from Zoho'}
          </button>
          <button 
            onClick={() => setShowAddModal(true)}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Lead
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search leads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="all">All Status</option>
            <option value="New">New</option>
            <option value="Contacted">Contacted</option>
            <option value="Qualified">Qualified</option>
            <option value="Proposal Sent">Proposal Sent</option>
            <option value="Negotiation">Negotiation</option>
            <option value="Won">Won</option>
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="updatedAt">Sort by Updated</option>
            <option value="createdAt">Sort by Created</option>
            <option value="name">Sort by Name</option>
            <option value="company">Sort by Company</option>
            <option value="status">Sort by Status</option>
          </select>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="desc">Newest First</option>
            <option value="asc">Oldest First</option>
          </select>
        </div>
      </div>

      {/* Leads Table - Zoho Style */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden relative">
        {/* Table loading overlay */}
        {tableLoading && (
          <div className="absolute inset-0 bg-white/50 dark:bg-gray-800/50 z-10 flex items-center justify-center">
            <div className="flex items-center gap-2 bg-white dark:bg-gray-700 px-4 py-2 rounded-lg shadow">
              <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
              <span className="text-sm text-gray-600 dark:text-gray-300">Updating...</span>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Company</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Lead Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Lead Source</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Lead Owner</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {displayLeads.length === 0 && !loading && !tableLoading ? (
                <tr>
                  <td colSpan="8" className="px-4 py-12 text-center">
                    <Users className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">
                      {debouncedSearch ? `No leads found for "${debouncedSearch}"` : 'No leads found'}
                    </p>
                  </td>
                </tr>
              ) : (
                displayLeads.map((lead, index) => (
                  <tr 
                    key={lead._id}
                    ref={index === displayLeads.length - 1 ? lastLeadRef : null}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                    onClick={() => navigate(`/leads/${lead._id}`)}
                  >
                    <td className="px-4 py-4">
                      <div className="flex items-center">
                        <Building className="w-4 h-4 mr-2 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{lead.company || '-'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{lead.name}</div>
                        {lead.designation && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">{lead.designation}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <a 
                        href={`mailto:${lead.email}`} 
                        className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {lead.email || '-'}
                      </a>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {lead.phone || lead.mobile || '-'}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{lead.source || '-'}</span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {lead.leadOwner?.name || lead.assignedTo?.name || '-'}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(lead.status)}`}>
                        {lead.status}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => navigate(`/leads/${lead._id}`)}
                          className="p-1 text-gray-400 hover:text-primary-600"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleContactLead(lead)}
                          className="p-1 text-gray-400 hover:text-green-600"
                          title="Contact"
                        >
                          <Mail className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedLead(lead)
                            setShowTagModal(true)
                          }}
                          className="p-1 text-gray-400 hover:text-blue-600"
                          title="Add Tag"
                        >
                          <Tag className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteLead(lead._id, lead)}
                          className={`p-1 ${lead.crmId ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-red-600'}`}
                          title={lead.crmId ? "Delete from Zoho CRM" : "Delete"}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Loading more indicator */}
        {loadingMore && (
          <div className="px-4 py-4 flex items-center justify-center border-t border-gray-200 dark:border-gray-600">
            <Loader2 className="w-5 h-5 animate-spin text-primary-600 mr-2" />
            <span className="text-sm text-gray-500 dark:text-gray-400">Loading more leads...</span>
          </div>
        )}
        {/* Total count */}
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Showing <span className="font-medium">{displayLeads.length}</span> of <span className="font-medium">{totalCount}</span> leads
            {debouncedSearch && <span> matching "{debouncedSearch}"</span>}
          </p>
        </div>
      </div>

      {/* Keep card view for mobile - hidden on larger screens */}
      <div className="md:hidden grid grid-cols-1 gap-4">
        {displayLeads.map((lead) => (
          <div
            key={`mobile-${lead._id}`}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
            onClick={() => navigate(`/leads/${lead._id}`)}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">{lead.name}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center">
                  <Building className="w-4 h-4 mr-1" />
                  {lead.company || 'No company'}
                </p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(lead.status)}`}>
                {lead.status}
              </span>
            </div>
            <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
              {lead.email && <div className="flex items-center"><Mail className="w-4 h-4 mr-2" />{lead.email}</div>}
              {(lead.phone || lead.mobile) && <div className="flex items-center"><Phone className="w-4 h-4 mr-2" />{lead.phone || lead.mobile}</div>}
              {lead.leadOwner?.name && <div className="flex items-center"><Users className="w-4 h-4 mr-2" />Owner: {lead.leadOwner.name}</div>}
            </div>
          </div>
        ))}
      </div>

      {/* Legacy card section - keeping for reference but hidden */}
      <div className="hidden legacy-grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {displayLeads.map((lead) => (
          <div
            key={`legacy-${lead._id}`}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">{lead.name}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center">
                  <Building className="w-4 h-4 mr-1" />
                  {lead.company || 'No company'}
                </p>
              </div>
              <div className={`px-2 py-1 rounded-lg ${getScoreColor(lead.score)}`}>
                <div className="flex items-center">
                  <Star className="w-4 h-4 mr-1" />
                  <span className="text-sm font-bold">{lead.score}</span>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <button 
                onClick={() => navigate(`/leads/${lead._id}`)}
                className="flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                <Eye className="w-3 h-3" />
                Details
              </button>
              <button 
                onClick={() => handleContactLead(lead)}
                className="flex items-center gap-1 px-3 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                <Mail className="w-3 h-3" />
                Contact
              </button>
              <button 
                onClick={() => {
                  setSelectedLead(lead)
                  setShowTagModal(true)
                }}
                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                title="Add Tag"
              >
                <Tag className="w-4 h-4" />
              </button>
              <button 
                onClick={() => handleDeleteLead(lead._id, lead)}
                className={`px-3 py-2 text-sm border rounded-lg ${lead.crmId ? 'border-gray-300 dark:border-gray-600 text-gray-400 cursor-not-allowed' : 'border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30'}`}
                title={lead.crmId ? "Delete from Zoho CRM" : "Delete Lead"}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}

        {displayLeads.length === 0 && !loading && !tableLoading && (
          <div className="col-span-full text-center py-12">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">No leads found</p>
          </div>
        )}
      </div>

      {/* Add Lead Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Add New Lead</h3>
            <form onSubmit={handleCreateLead} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
                <input
                  type="text"
                  value={newLead.name}
                  onChange={(e) => setNewLead({...newLead, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email *</label>
                <input
                  type="email"
                  value={newLead.email}
                  onChange={(e) => setNewLead({...newLead, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                <input
                  type="tel"
                  value={newLead.phone}
                  onChange={(e) => setNewLead({...newLead, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Company</label>
                <input
                  type="text"
                  value={newLead.company}
                  onChange={(e) => setNewLead({...newLead, company: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Designation</label>
                <input
                  type="text"
                  value={newLead.designation}
                  onChange={(e) => setNewLead({...newLead, designation: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Create Lead
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default Leads
